import React, { useEffect, useState } from "react";
import { PageHeader, Toast } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { versionLabel } from "@/lib/version";
import { lock } from "@/lib/perm";

/**
 * Parametrii > Licență (doar Admin, doar pe o instalare de client) — activarea
 * licenței din platformă, pentru cazul în care activarea din scriptul de
 * instalare a eșuat, plus sincronizarea manuală cu serverul central.
 * La activare, angajatorul și licențele se configurează automat.
 */
export default function LicenseActivation() {
  const { reloadLicensing, features, has } = useAuth();
  const [status, setStatus] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [updateConfirm, setUpdateConfirm] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null); // {kind: ok|info|error, text}
  const [form, setForm] = useState({ cui: "", activation_key: "" });
  const [busy, setBusy] = useState(false);
  // Pe o instalare deja activată, câmpurile sunt blocate până apeși „Reactivează”.
  const [unlocked, setUnlocked] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "info" });

  const load = () => api.get("/activation/status").then(r => {
    setStatus(r.data);
    window.dispatchEvent(new Event("license-status-changed"));
    if (r.data.activated) setForm({ cui: r.data.cui || "", activation_key: r.data.activation_key || "" });
  }).catch(e => setMsg({ text: e?.response?.data?.detail || "Eroare la încărcare", type: "error" }));
  const loadUpdateStatus = () => api.get("/updates/status").then(r => setUpdateStatus(r.data)).catch(() => {});
  useEffect(() => { load(); loadUpdateStatus(); }, []);

  const flash = (text, type) => { setMsg({ text, type }); if (type === "success") setTimeout(() => setMsg({ text: "" }), 3000); };

  const activate = async () => {
    if (!form.cui.trim() || !form.activation_key.trim()) return flash("CUI-ul și cheia de activare sunt obligatorii", "error");
    setBusy(true);
    try {
      await api.post("/activation/activate", { cui: form.cui.trim(), activation_key: form.activation_key.trim() });
      await reloadLicensing(); await load(); loadUpdateStatus();
      setUnlocked(false);
      flash("Licență activată. Angajatorul și licențele au fost configurate automat.", "success");
    } catch (e) { flash(e?.response?.data?.detail || "Activarea a eșuat", "error"); }
    finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      await api.post("/activation/sync-now");
      await reloadLicensing(); await load(); loadUpdateStatus();
      flash("Sincronizare reușită", "success");
    } catch (e) { flash(e?.response?.data?.detail || "Sincronizarea a eșuat", "error"); await load(); }
    finally { setBusy(false); }
  };

  // Buton separat de sincronizarea licenței: întreabă acum serverul dacă există o actualizare pentru acest client.
  const checkUpdates = async () => {
    setChecking(true); setCheckResult(null);
    try {
      const r = (await api.post("/updates/check")).data;
      await loadUpdateStatus();
      setCheckResult(r.available
        ? { kind: "info", text: `Există o actualizare disponibilă: versiunea ${r.version}. Detaliile și butonul „Preia update” sunt mai jos.` }
        : { kind: "ok", text: `Nu există actualizări. Folosiți ultima versiune disponibilă (${r.installed_version}).` });
    } catch (e) {
      setCheckResult({ kind: "error", text: e?.response?.data?.detail || "Verificarea actualizărilor a eșuat" });
    } finally { setChecking(false); }
  };

  const applyUpdate = async () => {
    if (updateConfirm.trim().toLowerCase() !== "da") return flash("Scrie 'da' în câmpul de confirmare", "error");
    setBusy(true);
    try {
      await api.post("/updates/apply", { confirm: "da" });
      setUpdateConfirm("");
      flash("Actualizarea a fost descărcată și începe în scurt timp. Platforma repornește singură și poate fi indisponibilă câteva minute; apoi revine, fără nicio altă acțiune din partea ta.", "success");
      loadUpdateStatus();
    } catch (e) { flash(e?.response?.data?.detail || "Actualizarea a eșuat", "error"); loadUpdateStatus(); }
    finally { setBusy(false); }
  };

  if (!status) return <div>Se încarcă...</div>;
  const fmt = (d) => d ? new Date(d).toLocaleString("ro-RO") : "—";

  return (
    <>
      <PageHeader title="Licență" subtitle="Activarea instalării și sincronizarea cu furnizorul" />
      <Toast msg={msg.text} type={msg.type} />

      <div className="card" style={{ padding: 22, marginBottom: 16 }} data-testid="license-status">
        {status.activated ? (
          <div className="grid-2" style={{ gap: 16 }}>
            <div><div className="label">Stare</div>
              {status.locked
                ? <span className="badge badge-rejected">Blocată — sincronizare eșuată de peste {status.grace_days} zile</span>
                : <span className="badge badge-active">Activă</span>}</div>
            <div><div className="label">CUI</div><div>{status.cui}</div></div>
            <div><div className="label">Ultima sincronizare reușită</div><div>{fmt(status.last_sync_ok_at)}</div></div>
            <div><div className="label">Ultima încercare</div>
              <div>{fmt(status.last_sync_at)} {status.last_sync_ok === false && <span style={{ color: "#B91C1C" }}>(eșuată)</span>}</div></div>
            {status.last_sync_error && <div style={{ gridColumn: "1 / -1", fontSize: 12, color: "#B91C1C" }}>{status.last_sync_error}</div>}
          </div>
        ) : (
          <div style={{ color: "#92400E", background: "#FEF3C7", padding: "10px 12px", borderRadius: 6, fontSize: 13 }}>
            Instalarea nu este încă activată. Introdu mai jos CUI-ul și cheia de activare primite de la furnizor —
            angajatorul și licențele se configurează automat, imediat după activare.
          </div>
        )}
        {status.activated && (
          <div style={{ marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={syncNow} data-testid="license-sync-now" {...lock(!busy && has("license.activation_manage"))}>Sincronizează acum</button>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 6 }}>Verifică în orice moment cu furnizorul valabilitatea licenței (nu caută actualizări).</div>
            {Object.entries(features.flags).some(([, on]) => on) && (
              <div style={{ fontSize: 12, color: "#334155", marginTop: 10 }} data-testid="active-features">
                Funcții speciale active pentru instalarea dvs.: {Object.entries(features.flags).filter(([, on]) => on).map(([k]) => features.labels[k] || k).join(", ")}
              </div>)}
          </div>
        )}
      </div>

      {status.activated && updateStatus?.applicable && (
        <div className="card" style={{ padding: 22, marginBottom: 16 }} data-testid="update-check-card">
          <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>Actualizări</div>
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>
            Versiune instalată: <b>{updateStatus.running_version ? versionLabel(updateStatus.running_version) : "—"}</b> ·{" "}
            <span data-testid="update-last-check">Ultima verificare: {updateStatus.checked_at ? fmt(updateStatus.checked_at) : "încă nu a rulat"}</span>
          </div>
          <button className="btn btn-secondary" onClick={checkUpdates} data-testid="update-check-button" {...lock(!(checking || busy) && has("updates.view"))}>
            {checking ? "Se verifică..." : "Verifică actualizări"}
          </button>
          {checkResult && (() => {
            const c = { error: ["#FEF2F2", "#991B1B"], ok: ["#F0FDF4", "#14532D"], info: ["var(--tint-50)", "var(--navy-3)"] }[checkResult.kind];
            return <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 6, fontSize: 13, background: c[0], color: c[1] }} data-testid="update-check-result">{checkResult.text}</div>;
          })()}
        </div>
      )}

      {updateStatus?.available && (
        <div className="card" style={{ padding: 22, marginBottom: 16 }} data-testid="update-available-card">
          <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>
            Actualizare disponibilă: v{updateStatus.version}
          </div>
          {updateStatus.changelog && (
            <pre style={{ fontSize: 12, color: "#334155", background: "#F8FAFC", padding: 10, borderRadius: 6,
                          whiteSpace: "pre-wrap", marginTop: 8 }}>{updateStatus.changelog}</pre>
          )}
          {updateStatus.status === "pending" ? (
            <div style={{ color: "#92400E", background: "#FEF3C7", padding: "10px 12px", borderRadius: 6, fontSize: 13, marginTop: 8 }}>
              Actualizarea se aplică acum: platforma repornește singură și poate fi indisponibilă câteva minute. Nu opri serverul cât timp durează.
            </div>
          ) : (
            <>
              {updateStatus.status === "failed" && updateStatus.status_detail && (
                <div style={{ color: "#B91C1C", background: "#FDE8E8", padding: "10px 12px", borderRadius: 6, fontSize: 13, marginTop: 8 }}>
                  Ultima încercare a eșuat: {updateStatus.status_detail}
                </div>
              )}
              <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
                <input className="input" placeholder='scrie "da" pentru a confirma' value={updateConfirm}
                  onChange={e => setUpdateConfirm(e.target.value)} style={{ maxWidth: 220 }} data-testid="update-confirm-input" />
                <button className="btn btn-primary" onClick={applyUpdate} data-testid="update-apply-button" {...lock(!busy && has("updates.apply"))}>
                  {busy ? "Se procesează..." : "Preia update"}
                </button>
              </div>
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 6 }}>
                Serverul se va opri, va reconstrui codul și va reporni automat — durează câteva minute.
                Baza de date și documentele încărcate nu sunt atinse.
              </div>
            </>
          )}
        </div>
      )}

      <div className="card" style={{ padding: 22 }}>
        <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 12 }}>{status.activated ? "Reactivare (cheie nouă)" : "Activare"}</div>
        {(() => {
          const locked = status.activated && !unlocked;
          return (<>
            <div className="grid-2" style={{ gap: 12 }}>
              <div><label className="label label-req">CUI-ul companiei</label>
                <input className="input" value={form.cui} disabled={locked} onChange={e => setForm({ ...form, cui: e.target.value })} data-testid="license-cui" /></div>
              <div><label className="label label-req">Cheia de activare</label>
                <input className="input" placeholder="XXXX-XXXX-XXXX-XXXX" value={form.activation_key} disabled={locked}
                  onChange={e => setForm({ ...form, activation_key: e.target.value })} data-testid="license-key" /></div>
            </div>
            {status.central_url && <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 8 }}>Server de licențiere: {status.central_url}</div>}
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              {locked ? (
                <button className="btn btn-secondary" onClick={() => setUnlocked(true)} disabled={busy} data-testid="license-unlock">Reactivează</button>
              ) : (<>
                <button className="btn btn-primary" onClick={activate} data-testid="license-activate" {...lock(!busy && has("license.activation_manage"))}>
                  {busy ? "Se procesează..." : (status.activated ? "Confirmă" : "Activează")}
                </button>
                {status.activated && (
                  <button className="btn btn-secondary" disabled={busy} data-testid="license-cancel"
                    onClick={() => { setUnlocked(false); setForm({ cui: status.cui || "", activation_key: status.activation_key || "" }); }}>Anulează</button>
                )}
              </>)}
            </div>
          </>);
        })()}
      </div>
    </>
  );
}
